import { IconButton } from 'rsuite'
import BrandLogo from '@/components/shared/brandLogo'
import SkipToContent from '@/components/shared/skipToContent'
import { TbWorld, TbMenu2, TbSearch } from 'react-icons/tb'
import { useRouter } from 'next/router'
import useTranslation from '@/hooks/useTranslation'
import style from './mobileHeader.module.scss'
import { getMobileHeaderTitle, getLanguageLabel } from '@/utils/headerHelper'
import { useSelector } from 'react-redux'
import { configSelectors } from '@/store/configs'
import { headerSelectors } from '@/store/header'
import { useState } from 'react'
import SearchModal from './searchModal'
import { customerHelper } from '@tweddle-se-team/common/constants'
import SubaruMobileMenuDrawer from './mobileMenuDrawer'
import NordicsMobileMenuDrawer from '@/components/themes/subaru-nordics/header/mobileMenuDrawer'
import ChangeVehicleIcon from '@/components/themes/common/icons/changeVehicleIcon'

const { CUSTOMER_IDS } = customerHelper

const canSearchByCustomer = {
  [CUSTOMER_IDS.SUBARU]: ({ brand, model, modelType, year }) => brand && model && modelType && year,
  [CUSTOMER_IDS.NORDICS]: ({ brand, model, modelType, year }) => brand && model && modelType && year
}

const mobileVehicleActionByCustomer = {
  [CUSTOMER_IDS.SUBARU]: ({ t, setMobileMenuOpen }) => ({
    id: 'btn-menu-mobile',
    icon: <TbMenu2 />,
    ariaLabel: t('header_menu', 'Menu'),
    onClick: () => setMobileMenuOpen(true)
  }),
  [CUSTOMER_IDS.NORDICS]: ({ t, setMobileMenuOpen }) => ({
    id: 'btn-changeVehicle-mobile-header',
    icon: <ChangeVehicleIcon />,
    ariaLabel: t('header_menu', 'Menu'),
    onClick: () => setMobileMenuOpen(true)
  })
}

const MobileMenuDrawerByCustomer = {
  [CUSTOMER_IDS.SUBARU]: SubaruMobileMenuDrawer,
  [CUSTOMER_IDS.NORDICS]: NordicsMobileMenuDrawer
}

const mobileHeaderClassByCustomer = {
  [CUSTOMER_IDS.SUBARU]: '',
  [CUSTOMER_IDS.NORDICS]: style.mobileHeaderNordics
}

function MobileHeader({
  returnToLandingPage = () => {},
  isOnline,
  customerId,
  locale,
  setShowLanguageSelector,
  resetVehicle,
  canSwitchLanguage,
  showLogoHeader = true
}) {
  const t = useTranslation()
  const router = useRouter()
  const { brand, model, year, modelType, image } = useSelector(headerSelectors.selectModel)
  const languages = useSelector(configSelectors.selectConfigByKey('languages'))
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showSearchModal, setShowSearchModal] = useState(false)

  const title = getMobileHeaderTitle({ year, model, modelType, customerId, t })
  const languageLabel = getLanguageLabel({ languages, locale, t })
  const canSearch = canSearchByCustomer[customerId] || canSearchByCustomer[CUSTOMER_IDS.SUBARU]
  const enableSearch = canSearch({ brand, model, modelType, year }) && isOnline
  const hasModel = !!model

  const handleChangeVehicle = () => {
    setMobileMenuOpen(false)
    if (resetVehicle) resetVehicle()
    router.push('/')
  }

  const handleLanguageClick = () => {
    setMobileMenuOpen(false)
    setShowLanguageSelector(true)
  }

  const getMobileVehicleAction =
    mobileVehicleActionByCustomer[customerId] || mobileVehicleActionByCustomer[CUSTOMER_IDS.SUBARU]
  const mobileVehicleAction = getMobileVehicleAction({ t, setMobileMenuOpen, handleChangeVehicle })
  const MobileMenuDrawer = MobileMenuDrawerByCustomer[customerId] || MobileMenuDrawerByCustomer[CUSTOMER_IDS.SUBARU]
  const customerHeaderClass =
    mobileHeaderClassByCustomer[customerId] || mobileHeaderClassByCustomer[CUSTOMER_IDS.SUBARU]

  return (
    <div className={`${style.mobileHeader} ${customerHeaderClass}`}>
      <SkipToContent />
      {/* Logo Row - Only shown when no model selected */}
      {showLogoHeader && !hasModel && (
        <div className={style.mobileLogoRow}>
          <BrandLogo
            brand="subaru"
            onClick={e => returnToLandingPage(e, isOnline)}
            height={36}
            width={120}
            className={style.mobileLogo}
          />
        </div>
      )}

      {/* Main Header Row */}
      <div
        className={
          hasModel ? `${style.mobileHeaderRow} ${style.mobileHeaderRowInverted} dark-section` : style.mobileHeaderRow
        }
      >
        {!!hasModel && (
          <IconButton
            id={mobileVehicleAction.id}
            className={style.mobileMenuBtn}
            icon={mobileVehicleAction.icon}
            size="md"
            appearance="ghost"
            aria-label={mobileVehicleAction.ariaLabel}
            onClick={mobileVehicleAction.onClick}
          />
        )}
        <span className={style.mobileHeaderTitle}>{title}</span>
        {enableSearch && (
          <IconButton
            id="btn-search-mobile"
            className={style.mobileSearchBtn}
            icon={<TbSearch />}
            size="md"
            appearance="ghost"
            aria-label={t('header_search', 'Search')}
            onClick={() => setShowSearchModal(!showSearchModal)}
          />
        )}
        {!hasModel && canSwitchLanguage && (
          <IconButton
            id="btn-language-mobile"
            className={style.mobileLanguageBtn}
            icon={<TbWorld />}
            size="md"
            appearance="ghost"
            onClick={() => setShowLanguageSelector(true)}
            aria-label={t('header_select_language', 'Select Website Language')}
          />
        )}
      </div>

      <MobileMenuDrawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        title={title}
        image={image}
        languageLabel={languageLabel}
        canSwitchLanguage={canSwitchLanguage}
        onChangeVehicle={handleChangeVehicle}
        onLanguageClick={handleLanguageClick}
        returnToLandingPage={returnToLandingPage}
        isOnline={isOnline}
        model={model}
        year={year}
      />

      {/* Search Modal */}
      {enableSearch && (
        <SearchModal
          open={showSearchModal}
          onClose={() => setShowSearchModal(false)}
          brand={brand}
          model={model}
          modelType={modelType}
          year={year}
          language={locale}
        />
      )}
    </div>
  )
}

export default MobileHeader
